/**
 * MasterAccessPage · v2.7.50+E26 Phase 1 (2026-02-28)
 * ═════════════════════════════════════════════════════════════════════
 * Universal Master Access control panel — accessible ONLY by the master
 * env-admin (the "primary system owner"). Lists every active staff
 * account and exposes a "Grant Universal Master Access" toggle that
 * flips the `is_super_root` flag on `db.drivers`.
 *
 * Backend contract (routers/super_root.py):
 *   GET  /api/admin/super-root/me     → { is_master_owner, is_super_root }
 *   GET  /api/admin/super-root/users  → [ { id, email, name, role,
 *                                          is_super_root,
 *                                          super_root_granted_by,
 *                                          super_root_granted_at } ]
 *   PUT  /api/admin/super-root/users/{id}  body: { enabled: bool }
 *
 * Token-refresh note · we deliberately do NOT bake `is_super_root` into
 * the JWT. The auth dependency re-reads the user's row on every request,
 * so toggling the flag takes effect on the target's NEXT API call. The
 * panel surfaces a tiny inline note to the operator confirming this.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldOff, Search, Loader2, AlertTriangle, Lock, ArrowLeft, Crown } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const ROLE_BADGE_COLORS = {
  admin:             "bg-amber-500/20 text-amber-300 border-amber-400/30",
  operating_partner: "bg-violet-500/20 text-violet-300 border-violet-400/30",
  developer:         "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/30",
  supervisor:        "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
  field_supervisor:  "bg-teal-500/20 text-teal-300 border-teal-400/30",
  dispatcher:        "bg-sky-500/20 text-sky-300 border-sky-400/30",
  team_leader:       "bg-indigo-500/20 text-indigo-300 border-indigo-400/30",
  driver:            "bg-zinc-500/20 text-zinc-300 border-zinc-400/30",
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function MasterAccessPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/super-root/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load users");
      setUsers([]);
    }
  }, []);

  // Owner gate + initial fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/super-root/me");
        if (cancelled) return;
        if (data?.is_master_owner) {
          setAuthorized(true);
          await loadUsers();
        } else {
          setAuthorized(false);
        }
      } catch {
        if (!cancelled) setAuthorized(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadUsers]);

  const onToggle = useCallback(async (user, nextEnabled) => {
    const verb = nextEnabled ? "Grant" : "Revoke";
    const ok = window.confirm(
      `${verb} Universal Master Access for ${user.email}?\n\n` +
      (nextEnabled
        ? "They will gain UNCONDITIONAL read/write access to every admin surface " +
          "(Live Site Configurations, Email Administration, API/Developer Settings, " +
          "and System Logs)."
        : "Their elevated role and existing assignments remain unchanged — only " +
          "the universal bypass is removed.")
    );
    if (!ok) return;
    setPendingId(user.id);
    try {
      const { data } = await adminApi.put(
        `/admin/super-root/users/${encodeURIComponent(user.id)}`,
        { enabled: nextEnabled }
      );
      setUsers((prev) => prev.map((u) =>
        u.id === user.id
          ? { ...u,
              is_super_root: data.is_super_root,
              super_root_granted_by: data.granted_by || "",
              super_root_granted_at: data.granted_at || "" }
          : u
      ));
      toast.success(
        nextEnabled
          ? `Master access granted to ${user.email}`
          : `Master access revoked from ${user.email}`,
        {
          description:
            "Privileges take effect on the user's next API request — they do not need to log out.",
        }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setPendingId(null);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300">
        <Loader2 className="h-5 w-5 animate-spin mr-2"/>
        <span>Checking master-owner credentials…</span>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-200 p-6" data-testid="master-access-denied">
        <Lock className="h-12 w-12 text-rose-400 mb-3"/>
        <h1 className="text-xl font-bold mb-1">Access Denied</h1>
        <p className="text-sm text-zinc-400 mb-4 max-w-md text-center">
          The Master Access control panel is reserved for the primary system owner.
          Only the master env-admin can grant Universal Master Access to other accounts.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate("/admin")}
          data-testid="master-access-denied-back"
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4"/>Back to Admin Console
        </Button>
      </div>
    );
  }

  const grantedCount = users.filter((u) => u.is_super_root).length;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="master-access-page">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
          className="mb-3 text-zinc-400 hover:text-zinc-100 -ml-2"
          data-testid="master-access-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1"/>Back to Admin Console
        </Button>
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-amber-500/10 border border-amber-400/30 p-2.5 flex-shrink-0">
            <Crown className="h-7 w-7 text-amber-400"/>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide text-amber-300">
              Master Access Control
            </h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
              Grant Universal Master Access to any active staff account. Recipients
              gain owner-equivalent reach across every admin surface (Live Site
              Configurations, Email Administration, API / Developer Settings, and
              System Logs) — bypassing all granular role gates.
            </p>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="max-w-5xl mx-auto mb-4 flex flex-wrap items-center gap-3">
        <Card className="bg-zinc-900 border-zinc-800 px-3 py-2 flex items-center gap-2" data-testid="master-access-stat-granted">
          <ShieldCheck className="h-4 w-4 text-amber-400"/>
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Granted</div>
          <div className="text-lg font-extrabold text-amber-300 ml-1">{grantedCount}</div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 px-3 py-2 flex items-center gap-2" data-testid="master-access-stat-total">
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Active users</div>
          <div className="text-lg font-extrabold text-zinc-100 ml-1">{users.length}</div>
        </Card>
        <div className="flex-1"/>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by email, name, or role…"
            className="pl-8 bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="master-access-search"
          />
        </div>
      </div>

      {/* User table */}
      <div className="max-w-5xl mx-auto">
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="master-access-table">
              <thead className="bg-zinc-950/60 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                <tr>
                  <th className="text-left px-4 py-2.5">User</th>
                  <th className="text-left px-4 py-2.5">Role</th>
                  <th className="text-left px-4 py-2.5">Granted by · When</th>
                  <th className="text-right px-4 py-2.5">Master Access</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-500 italic">
                    {users.length === 0 ? "No active staff accounts found." : "No users match your search."}
                  </td></tr>
                )}
                {filtered.map((u) => {
                  const isPending = pendingId === u.id;
                  const roleClass = ROLE_BADGE_COLORS[u.canonical_role] || ROLE_BADGE_COLORS.driver;
                  const isProtected = u.is_protected;
                  return (
                    <tr
                      key={u.id}
                      className={`border-b border-zinc-800/60 hover:bg-zinc-800/30 transition-colors ${u.is_super_root ? "bg-amber-500/5" : ""}`}
                      data-testid={`master-access-row-${u.id}`}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {u.is_super_root && (
                            <ShieldCheck className="h-4 w-4 text-amber-400 flex-shrink-0" title="Has Universal Master Access"/>
                          )}
                          <div className="min-w-0">
                            <div className="font-bold text-zinc-100 truncate">{u.name || "—"}</div>
                            <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                          </div>
                          {isProtected && (
                            <span title="Protected account" className="text-[9px] uppercase font-bold text-rose-300 border border-rose-400/30 bg-rose-500/10 px-1.5 py-0.5 rounded">
                              Protected
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] uppercase tracking-widest font-bold border px-1.5 py-0.5 rounded ${roleClass}`}>
                          {u.canonical_role || u.role || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-zinc-400 text-xs">
                        {u.is_super_root ? (
                          <>
                            <div className="font-mono">{u.super_root_granted_by || "—"}</div>
                            <div className="text-zinc-500">{formatTimestamp(u.super_root_granted_at)}</div>
                          </>
                        ) : (
                          <span className="text-zinc-600 italic">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-3">
                          {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin text-amber-300"/>
                          ) : null}
                          <Switch
                            checked={!!u.is_super_root}
                            onCheckedChange={(v) => onToggle(u, v)}
                            disabled={isPending}
                            data-testid={`master-access-toggle-${u.id}`}
                            aria-label={`Grant Universal Master Access to ${u.email}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
        <p className="text-xs text-zinc-500 italic mt-3 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500/70"/>
          Every grant / revoke is audit-logged with actor, target, and timestamp.
          Privileges take effect on the user&rsquo;s next API request — no re-login required.
        </p>
      </div>
    </div>
  );
}
